/**
 * Bank code → full bank name for bank-logo alt text, keyed by `MARKET|CODE` (NO, SE, FI).
 * DK isn't here: its names come from `BANK_REGISTRY` in `@axo/config/banks/bank-registry`.
 *
 * Source: the warehouse lender name (`op_mvs_rs_mcp.tbl_statuses_l1y.bank`, the most frequent
 *   name per market and code), as copied in `packages/personalisation-engine/src/reference/bank-names.ts`,
 *   for every code with a logo in `@axo/config/banks/bank-codes`. Entries marked `logo` come
 *   from the logo SVG's own wordmark (`<app>/public/partners/bank/<market>/<code>.svg`): either a
 *   name the warehouse lacks, or a warehouse name cleaned to the wordmark.
 * Captured: warehouse names 2026-06-24 (MV-2938); logo wordmarks read 2026-10-19 (MV-3806),
 *   FI|P1 read 2026-10-29 (MV-3849), SE|PNL read 2026-11-04 (MV-3882).
 * Stale when: a lender is added to `bank-codes` (its code falls back to the code as alt text
 *   until it's named here), or a lender rebrands or ships a new logo.
 * Refresh: by hand. For a new or rebranded code, take the name from the warehouse map above,
 *   or read it off the logo SVG and mark it `logo`. Never guess a name.
 *
 * Unmapped on purpose (the logo is a symbol with no text): FI ALD, MBL, OPF, STP.
 */
export const BANK_NAMES: Readonly<Record<string, string>> = {
  'FI|AKT': 'Aktia',
  'FI|AVI': 'Avida',
  'FI|BN': 'Bank Norwegian',
  'FI|BON': 'Bondora',
  'FI|BRA': 'Lea Bank',
  'FI|BRO': 'Brocc',
  'FI|CRD': 'TF Bank',
  'FI|CRR': 'TF Bank',
  'FI|DBA': 'Danske Bank', // logo
  'FI|FEF': 'Alisa Pankki', // logo
  'FI|FEM': 'Ferratum', // logo
  'FI|FIX': 'Fixura',
  'FI|GFM': 'GF Money',
  'FI|HAL': 'Halino',
  'FI|HIT': 'Hightrust.id', // logo
  'FI|IB': 'Instabank', // logo (warehouse: "InstaBank")
  'FI|KAR': 'Kaarna',
  'FI|KB': 'Morrow Bank',
  'FI|NDA': 'Nordea', // logo
  'FI|NDX': 'Nordax Bank', // logo (warehouse: "Nordax")
  'FI|NOR': 'Northmill', // logo
  'FI|OMP': 'OmaSP', // logo
  'FI|P1': 'Plus1', // logo
  'FI|PAN': 'S-Pankki', // logo
  'FI|POP': 'Pop Pankki',
  'FI|RB': 'Resurs Bank',
  'FI|REM': 're:member', // logo
  'FI|SAL': 'Saldo Bank',
  'FI|SRC': 'Saldo Bank',
  'FI|SVK': 'Svea',

  'NO|AVI': 'Avida',
  'NO|BA2': 'Bank2', // logo
  'NO|BB': 'Balanse Bank',
  'NO|BBB': 'TF Bank',
  'NO|BN': 'Bank Norwegian',
  'NO|BRA': 'Lea Bank',
  'NO|BST': 'Bluestep Bank', // logo
  'NO|EB': 'Enity Bank',
  'NO|EIK': 'Eika', // logo
  'NO|EKS': 'Express Bank', // logo
  'NO|ESY': 'Easybank', // logo
  'NO|FCB': 'Facit Bank', // logo
  'NO|FEM': 'Ferratum',
  'NO|GB': 'Nordea',
  'NO|IB': 'Instabank',
  'NO|IKA': 'Ikano Bank',
  'NO|KB': 'Morrow Bank',
  'NO|KF': 'Kraft Bank',
  'NO|MB': 'Monobank', // logo
  'NO|NDO': 'Nordax Bank', // logo
  'NO|NDX': 'Nordax Bank', // logo (warehouse: "Nordax")
  'NO|NOR': 'Northmill',
  'NO|RB': 'Resurs Bank', // logo
  'NO|REM': 'Remember',
  'NO|SCB': 'Santander',
  'NO|SVF': 'Svea Bank',
  'NO|THR': 'Thorn',
  'NO|YA': 'YA Lånet', // logo

  'SE|AVI': 'Avida', // logo
  'SE|BAL': 'Balanzia', // logo
  'SE|BKB': 'Banky Borgenslån',
  'SE|BKY': 'Banky',
  'SE|BN': 'Bank Norwegian',
  'SE|BRA': 'Lea Bank',
  'SE|BRI': 'Brixo Kontokredit',
  'SE|BRO': 'Brocc',
  'SE|BRX': 'Brixo Privatlån',
  'SE|BST': 'Bluestep', // logo
  'SE|CBY': 'Cash Buddy', // logo
  'SE|COL': 'Collector', // logo
  'SE|CRD': 'Credento', // logo
  'SE|EQL': 'Equilo',
  'SE|ERL': 'Euroloan', // logo
  'SE|FCB': 'Facit Bank',
  'SE|FEM': 'Ferratum',
  'SE|FLX': 'Flexkontot', // logo
  'SE|FRI': 'Friia', // logo
  'SE|FRL': 'Fairlo', // logo
  'SE|FT': 'Frogtail', // logo
  'SE|FX': 'Forex Bank',
  'SE|IB': 'Instabank', // logo
  'SE|ICA': 'ICA Banken', // logo (warehouse: "Ica")
  'SE|IKA': 'Ikano Bank', // logo (warehouse: "Ikano")
  'SE|KB': 'Morrow Bank',
  'SE|LDF': 'Lunar', // logo
  'SE|LSB': 'Lån & Spar Bank', // logo (warehouse: "Lån og Spar")
  'SE|LTP': 'Loanstep',
  'SE|MG': 'Marginalen',
  'SE|MMA': 'MedMera',
  'SE|MOA': 'Moank', // logo
  'SE|MOG': 'MoneyGo', // logo
  'SE|NDX': 'Nordax Bank', // logo (warehouse: "Nordax")
  'SE|NNK': 'Konsumentkredit', // logo
  'SE|NNT': 'Nordnet', // logo
  'SE|NOC': 'Northmill Kontokredit',
  'SE|NOP': 'Northmill Privatlån', // logo
  'SE|NSF': 'Plus 1 Nylånet',
  'SE|NSM': 'Plus 1 Samlingslånet',
  'SE|PNL': 'Pantlån', // logo
  'SE|RB': 'Resurs Bank',
  'SE|REM': 'Remember',
  'SE|SAL': 'Saldo Bank', // logo (warehouse: "Saldo")
  'SE|SDF': 'SevenDay Bank', // logo
  'SE|SPW': 'Spendwise Bank',
  'SE|ST': 'Santander',
  'SE|SVK': 'Svea',
  'SE|SVP': 'Svea',
  'SE|TAN': 'Tando', // logo
  'SE|TF': 'TF Bank', // logo
  'SE|THR': 'Thorn',
  'SE|TRK': 'Tryggkredit',
  'SE|WAS': 'Wasa Kredit', // logo
  'SE|WGF': 'Wästgöta Finans', // logo
};
